import React from 'react'
import Skeleton from 'Components/Skeleton'
import { cn } from '../../lib/utils'
import { useLazyList } from './useLazyList'
import AccountLazySentinel from './AccountLazySentinel'

const ALIGN_CLASS = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right'
}

const columnVisibilityClass = column => {
  if (column.hideBelow === 'md') return 'hidden md:table-cell'
  if (column.hideBelow === 'sm') return 'hidden sm:table-cell'
  if (column.hideAbove === 'md') return 'md:hidden'
  if (column.hideAbove === 'sm') return 'sm:hidden'
  return ''
}

const ProfileTable = ({
  columns = [],
  rows = [],
  getRowId,
  onRowClick,
  emptyMessage = 'No items found',
  isLoading = false,
  loadingRowCount = 4,
  footer = null,
  className,
  lazy = true,
  pageSize
}) => {
  const visibleColumns = columns.filter(Boolean)
  const sourceRows = isLoading
    ? Array.from({ length: loadingRowCount }, (_, index) => ({
        __skeleton: true,
        id: `skeleton-${index}`
      }))
    : rows
  const shouldLazy = lazy && !isLoading
  const { visibleItems, sentinelRef, hasMore } = useLazyList(
    sourceRows,
    shouldLazy ? pageSize : Math.max(sourceRows.length, 1)
  )
  const displayRows = shouldLazy ? visibleItems : sourceRows
  const clickable = Boolean(onRowClick) && !isLoading

  return (
    <div className={cn('w-full', className)}>
      <div className="overflow-x-auto">
        <table className="w-full min-w-full border-collapse text-left">
          <thead>
            <tr className="border-b border-border">
              {visibleColumns.map(column => (
                <th
                  key={column.id}
                  scope="col"
                  className={cn(
                    'px-4 py-3 text-xs font-semibold uppercase tracking-wide text-secondary-foreground',
                    ALIGN_CLASS[column.align] || ALIGN_CLASS.left,
                    columnVisibilityClass(column),
                    column.headerClassName
                  )}
                >
                  {column.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {!isLoading && sourceRows.length === 0 ? (
              <tr>
                <td
                  colSpan={visibleColumns.length || 1}
                  className="px-4 py-10 text-sm font-medium text-secondary-foreground"
                >
                  <div className="rounded-lg bg-foreground/10 px-4 py-8 text-center">
                    {emptyMessage}
                  </div>
                </td>
              </tr>
            ) : (
              displayRows.map((row, rowIndex) => {
                const rowId = row.__skeleton
                  ? row.id
                  : getRowId
                  ? getRowId(row)
                  : row.id ?? rowIndex

                return (
                  <tr
                    key={rowId}
                    className={cn(
                      'border-b border-[hsl(var(--border)/0.45)] last:border-b-0',
                      clickable &&
                        'account-interactive-row cursor-pointer hover:bg-foreground/10'
                    )}
                    onClick={
                      clickable
                        ? () => {
                            onRowClick(row)
                          }
                        : undefined
                    }
                  >
                    {visibleColumns.map(column => (
                      <td
                        key={column.id}
                        className={cn(
                          'px-4 py-4 align-middle text-sm text-foreground',
                          ALIGN_CLASS[column.align] || ALIGN_CLASS.left,
                          columnVisibilityClass(column),
                          column.className
                        )}
                        onClick={
                          column.stopRowClick
                            ? event => event.stopPropagation()
                            : undefined
                        }
                      >
                        {row.__skeleton ? (
                          column.renderSkeleton ? (
                            column.renderSkeleton(rowIndex)
                          ) : (
                            <Skeleton
                              width={column.skeletonWidth || '72%'}
                              height={column.skeletonHeight || '20px'}
                              borderRadius="4px"
                            />
                          )
                        ) : (
                          column.render?.(row, rowIndex)
                        )}
                      </td>
                    ))}
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>
      {shouldLazy ? (
        <AccountLazySentinel sentinelRef={sentinelRef} hasMore={hasMore} />
      ) : null}
      {footer}
    </div>
  )
}

export default ProfileTable
